import React from "react";
import { IKO } from "@/design-system";

/**
 * Iter 28 · Vania Msg 232 — flat, faceless, Nairobi-feeling illustration set
 * for the intake moment cards. Same visual language across all 24: two-tone,
 * simple geometric silhouettes, tinted with each iko's colour, no text.
 *
 * Not a photo — a small illustrated tile that reads instantly at 150×80.
 * Photos will replace these later; the SVG API stays the same.
 */
export default function MomentIllustration({ momentKey, iko, on }) {
  const t = IKO[iko] || {};
  const bg = t.companion || "#2F2F52";     // deep tint
  const stroke = t.bright || "#FFD60A";    // accent
  const light = "rgba(255,255,255,0.18)";  // sky/moon/sun fill
  const dark = "rgba(26,26,48,0.55)";      // ground shadow
  const Comp = MAP[momentKey] || Generic;

  return (
    <svg viewBox="0 0 200 100" preserveAspectRatio="xMidYMid slice"
         style={{ width: "100%", height: "100%", display: "block" }}
         aria-hidden="true">
      <rect width="200" height="100" fill={on ? "#FFD60A" : bg} />
      {/* soft horizon glow */}
      <ellipse cx="100" cy="90" rx="140" ry="34" fill={on ? "rgba(26,26,48,0.10)" : "rgba(255,255,255,0.05)"} />
      <Comp stroke={on ? "#1A1A30" : stroke} light={on ? "rgba(26,26,48,0.15)" : light} dark={on ? "rgba(26,26,48,0.35)" : dark} />
    </svg>
  );
}

/* --------------------------- moment-specific glyphs --------------------------- */
// All glyphs share: 1.6-2px strokes, no faces, iko tint + a light accent.

const Line = ({ x1, y1, x2, y2, stroke, w = 2 }) => <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={stroke} strokeWidth={w} strokeLinecap="round" />;
const CircleFig = ({ cx, cy, r, stroke, fill = "none", w = 2 }) => <circle cx={cx} cy={cy} r={r} stroke={stroke} strokeWidth={w} fill={fill} />;
const Body = ({ x, stroke }) => (
  // headless silhouette from waist up
  <g>
    <circle cx={x} cy={40} r={6} fill={stroke} />
    <path d={`M${x - 8} 80 Q${x} 46 ${x + 8} 80`} stroke={stroke} strokeWidth={2} fill="none" strokeLinecap="round" />
  </g>
);

function SkyMoon({ stroke, light }) { return <><circle cx={160} cy={26} r={12} fill={light} /><Line x1={22} y1={22} x2={26} y2={22} stroke={stroke} /><Line x1={44} y1={30} x2={48} y2={30} stroke={stroke} /></>; }
function SkySun({ stroke, light })  { return <><circle cx={160} cy={30} r={14} fill={light} /><circle cx={160} cy={30} r={8} fill={stroke} opacity="0.35" /></>; }
function Ground({ dark }) { return <rect x="0" y="82" width="200" height="18" fill={dark} />; }

const Dance = ({ stroke, light, dark }) => (<><SkyMoon stroke={stroke} light={light} /><Ground dark={dark} />
  <g><CircleFig cx={70} cy={42} r={6} stroke={stroke} fill={stroke} /><path d="M62 82 L70 50 L78 82" stroke={stroke} strokeWidth={2.4} fill="none" strokeLinecap="round" />
  <path d="M55 60 Q70 40 90 55" stroke={stroke} strokeWidth={2} fill="none" strokeLinecap="round" /></g>
  <g><CircleFig cx={128} cy={40} r={6} stroke={stroke} fill={stroke} /><path d="M122 82 L128 48 L134 82" stroke={stroke} strokeWidth={2.4} fill="none" strokeLinecap="round" />
  <path d="M116 62 Q128 44 148 58" stroke={stroke} strokeWidth={2} fill="none" strokeLinecap="round" /></g>
</>);
const Gig = ({ stroke, light, dark }) => (<><Ground dark={dark} />
  <g><rect x="80" y="34" width="40" height="40" fill={light} rx={2} /><Line x1={80} y1={44} x2={120} y2={44} stroke={stroke} />
  <Line x1={100} y1={44} x2={100} y2={70} stroke={stroke} /><rect x="86" y="60" width="8" height="14" fill={stroke} /><rect x="106" y="60" width="8" height="14" fill={stroke} /></g>
</>);
const DayParty = ({ stroke, light, dark }) => (<><SkySun stroke={stroke} light={light} /><Ground dark={dark} /><Body x={70} stroke={stroke} /><Body x={100} stroke={stroke} /><Body x={130} stroke={stroke} /></>);
const Karaoke = ({ stroke, light, dark }) => (<><SkyMoon stroke={stroke} light={light} /><Ground dark={dark} />
  <g><rect x="94" y="30" width="12" height="26" rx={6} fill={stroke} /><Line x1={100} y1={56} x2={100} y2={74} stroke={stroke} w={3} /><ellipse cx={100} cy={78} rx={12} ry={4} fill={stroke} /></g>
</>);
const RoofParty = ({ stroke, light, dark }) => (<><SkySun stroke={stroke} light={light} /><rect x="30" y="60" width="140" height="8" fill={stroke} /><Body x={70} stroke={stroke} /><Body x={130} stroke={stroke} /><Ground dark={dark} /></>);
const Festival = ({ stroke, light, dark }) => (<><SkyMoon stroke={stroke} light={light} /><Ground dark={dark} />
  <polygon points="30,80 60,50 90,80" fill={light} stroke={stroke} strokeWidth={1.8} />
  <polygon points="110,80 140,45 170,80" fill={light} stroke={stroke} strokeWidth={1.8} />
</>);
const Jazz = ({ stroke, light, dark }) => (<><SkyMoon stroke={stroke} light={light} /><Ground dark={dark} />
  <CircleFig cx={70} cy={60} r={12} stroke={stroke} fill="none" w={2.4} />
  <Line x1={82} y1={62} x2={126} y2={30} stroke={stroke} w={2.4} /><ellipse cx={128} cy={30} rx={6} ry={4} fill={stroke} />
</>);
const Drinks = ({ stroke, light, dark }) => (<><Ground dark={dark} />
  <path d="M80 30 L120 30 L110 60 Q100 75 90 60 Z" fill={light} stroke={stroke} strokeWidth={2} />
  <Line x1={100} y1={60} x2={100} y2={78} stroke={stroke} /><ellipse cx={100} cy={82} rx={14} ry={3} fill={stroke} />
</>);
const Gallery = ({ stroke, light, dark }) => (<><Ground dark={dark} />
  <rect x="40" y="30" width="30" height="40" fill={light} stroke={stroke} strokeWidth={2} />
  <rect x="90" y="30" width="30" height="40" fill={light} stroke={stroke} strokeWidth={2} />
  <rect x="140" y="30" width="30" height="40" fill={light} stroke={stroke} strokeWidth={2} />
</>);
const Brunch = ({ stroke, light, dark }) => (<><SkySun stroke={stroke} light={light} /><Ground dark={dark} />
  <ellipse cx={100} cy={72} rx={40} ry={6} fill={stroke} /><CircleFig cx={100} cy={64} r={16} stroke={stroke} fill={light} w={2} />
</>);
const Sunset = ({ stroke, light, dark }) => (<><circle cx={100} cy={82} r={38} fill={light} /><Line x1={0} y1={82} x2={200} y2={82} stroke={stroke} w={2} /><Ground dark={dark} /></>);
const Market = ({ stroke, light, dark }) => (<><SkySun stroke={stroke} light={light} /><Ground dark={dark} />
  <rect x="30" y="50" width="50" height="24" fill={light} stroke={stroke} strokeWidth={2} />
  <rect x="90" y="50" width="50" height="24" fill={light} stroke={stroke} strokeWidth={2} />
  <polygon points="30,50 55,38 80,50" fill={stroke} /><polygon points="90,50 115,38 140,50" fill={stroke} />
</>);
const Hike = ({ stroke, light, dark }) => (<><SkySun stroke={stroke} light={light} /><Ground dark={dark} />
  <polygon points="20,82 70,40 120,82" fill={light} stroke={stroke} strokeWidth={2} />
  <polygon points="100,82 150,50 180,82" fill={light} stroke={stroke} strokeWidth={2} />
</>);
const Match = ({ stroke, light, dark }) => (<><Ground dark={dark} />
  <CircleFig cx={70} cy={60} r={16} stroke={stroke} fill={light} w={2} />
  <path d="M60 60 L70 52 L80 60 L80 70 L70 76 L60 70 Z" stroke={stroke} strokeWidth={1.6} fill="none" />
  <rect x="130" y="40" width="6" height="34" fill={stroke} /><Line x1={130} y1={40} x2={160} y2={48} stroke={stroke} />
</>);
const Games = ({ stroke, light, dark }) => (<><Ground dark={dark} />
  <rect x="72" y="42" width="28" height="28" fill={light} stroke={stroke} strokeWidth={2} rx={4} />
  <CircleFig cx={80} cy={50} r={2} stroke={stroke} fill={stroke} /><CircleFig cx={92} cy={62} r={2} stroke={stroke} fill={stroke} />
  <rect x="112" y="42" width="28" height="28" fill={light} stroke={stroke} strokeWidth={2} rx={4} />
  <CircleFig cx={120} cy={62} r={2} stroke={stroke} fill={stroke} /><CircleFig cx={132} cy={50} r={2} stroke={stroke} fill={stroke} />
</>);
const RunClub = ({ stroke, light, dark }) => (<><SkySun stroke={stroke} light={light} /><Ground dark={dark} />
  <g transform="translate(70,44)"><CircleFig cx={0} cy={0} r={5} stroke={stroke} fill={stroke} /><path d="M-8 30 L0 8 L10 24 L4 34" stroke={stroke} strokeWidth={2} fill="none" strokeLinecap="round" /></g>
  <g transform="translate(120,44)"><CircleFig cx={0} cy={0} r={5} stroke={stroke} fill={stroke} /><path d="M-8 30 L0 8 L10 24 L4 34" stroke={stroke} strokeWidth={2} fill="none" strokeLinecap="round" /></g>
</>);
const Padel = ({ stroke, light, dark }) => (<><Ground dark={dark} />
  <rect x="30" y="30" width="140" height="44" fill={light} stroke={stroke} strokeWidth={2} />
  <Line x1={100} y1={30} x2={100} y2={74} stroke={stroke} /><CircleFig cx={140} cy={52} r={4} stroke={stroke} fill={stroke} />
</>);
const BigGame = ({ stroke, light, dark }) => (<><Ground dark={dark} />
  <rect x="50" y="26" width="100" height="46" fill={light} stroke={stroke} strokeWidth={2} rx={4} />
  <CircleFig cx={100} cy={50} r={10} stroke={stroke} fill={stroke} />
</>);
const Talk = ({ stroke, light, dark }) => (<><Ground dark={dark} />
  <rect x="60" y="30" width="80" height="10" fill={light} stroke={stroke} strokeWidth={2} />
  <Line x1={70} y1={40} x2={70} y2={76} stroke={stroke} /><Line x1={130} y1={40} x2={130} y2={76} stroke={stroke} />
  <Body x={100} stroke={stroke} />
</>);
const Founders = ({ stroke, light, dark }) => (<><SkySun stroke={stroke} light={light} /><Ground dark={dark} />
  <rect x="70" y="50" width="60" height="20" fill={light} stroke={stroke} strokeWidth={2} rx={2} />
  <path d="M74 50 Q78 40 82 50" stroke={stroke} strokeWidth={1.6} fill="none" />
  <path d="M96 50 Q100 40 104 50" stroke={stroke} strokeWidth={1.6} fill="none" />
  <path d="M118 50 Q122 40 126 50" stroke={stroke} strokeWidth={1.6} fill="none" />
</>);
const Networking = ({ stroke, light, dark }) => (<><Ground dark={dark} /><Body x={60} stroke={stroke} /><Body x={100} stroke={stroke} /><Body x={140} stroke={stroke} /><Line x1={68} y1={50} x2={92} y2={50} stroke={stroke} /><Line x1={108} y1={50} x2={132} y2={50} stroke={stroke} /></>);
const Masterclass = ({ stroke, light, dark }) => (<><Ground dark={dark} />
  <rect x="40" y="26" width="120" height="30" fill={light} stroke={stroke} strokeWidth={2} />
  <Line x1={50} y1={38} x2={130} y2={38} stroke={stroke} w={1.6} /><Line x1={50} y1={46} x2={100} y2={46} stroke={stroke} w={1.6} />
</>);
const Pitch = ({ stroke, light, dark }) => (<><Ground dark={dark} />
  <path d="M60 74 L100 40 L140 74 Z" fill={light} stroke={stroke} strokeWidth={2} />
  <Line x1={100} y1={40} x2={100} y2={26} stroke={stroke} /><CircleFig cx={100} cy={22} r={4} stroke={stroke} fill={stroke} />
</>);
const Coworking = ({ stroke, light, dark }) => (<><SkySun stroke={stroke} light={light} /><Ground dark={dark} />
  <rect x="40" y="50" width="120" height="22" fill={light} stroke={stroke} strokeWidth={2} />
  <rect x="56" y="38" width="24" height="14" fill={stroke} /><rect x="120" y="38" width="24" height="14" fill={stroke} />
</>);
function Generic({ stroke, light, dark }) { return <><Ground dark={dark} /><CircleFig cx={100} cy={50} r={18} stroke={stroke} fill={light} w={2} /></>; }

const MAP = {
  saturday_night_dance_floor: Dance,
  late_night_gig: Gig,
  amapiano_day_party: DayParty,
  karaoke_night_crew: Karaoke,
  friday_rooftop_party: RoofParty,
  festival_weekend: Festival,
  tuesday_jazz_night: Jazz,
  friday_drinks_after_work: Drinks,
  saturday_afternoon_gallery: Gallery,
  sunday_brunch_runs_long: Brunch,
  rooftop_at_sunset: Sunset,
  market_day: Market,
  saturday_morning_hike: Hike,
  sunday_afternoon_match: Match,
  games_night_with_friends: Games,
  morning_run_club: RunClub,
  padel_after_work: Padel,
  big_game_sports_bar: BigGame,
  weeknight_talk_or_workshop: Talk,
  founders_breakfast: Founders,
  lunchtime_networking: Networking,
  weekend_masterclass: Masterclass,
  pitch_night: Pitch,
  creative_coworking_day: Coworking,
};
